import React, { useRef, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "moment";
import { includes } from "lodash";
import { isDynamicDate } from "@/services/parameters/DateParameter";
import DateInput from "@/components/date-inputs/DateInput";
import DateTimeInput from "@/components/date-inputs/DateTimeInput";
import DynamicButton from "@/components/dynamic-parameters/DynamicButton";
import { useTheme } from "@/components/ThemeProvider";

import "./DynamicParameters.less";

function DynamicDatePicker({ type, value, className, dateOptions, dynamicButtonOptions, onSelect, parameter }) {
  const { isDarkMode } = useTheme();
  const dateComponentRef = useRef();
  const hasDynamicValue = isDynamicDate(value);
  const isDateTime = includes(type, "datetime");

  const additionalAttributes = {};

  let DateComponent = DateInput;
  if (isDateTime) {
    DateComponent = DateTimeInput;
    if (includes(type, "with-seconds")) {
      additionalAttributes.withSeconds = true;
    }
  }

  if (moment.isMoment(value) || value === null) {
    additionalAttributes.value = value;
  }

  if (hasDynamicValue) {
    const dynamicDate = value;
    additionalAttributes.placeholder = dynamicDate && dynamicDate.name;
    additionalAttributes.value = null;
  }

  const onDynamicValueSelect = useCallback((dynamicValue) => {
    onSelect(dynamicValue);
  }, [onSelect]);

  return (
    <div className={classNames("date-parameter", className, { "dark-mode": isDarkMode })}>
      <DateComponent
        {...dateOptions}
        ref={dateComponentRef}
        className={classNames("redash-datepicker", type, { "dynamic-value": hasDynamicValue })}
        onSelect={onSelect}
        suffixIcon={null}
        {...additionalAttributes}
      />
      <DynamicButton
        options={dynamicButtonOptions.options}
        staticValueLabel={dynamicButtonOptions.staticValueLabel}
        selectedDynamicValue={hasDynamicValue ? value : null}
        enabled={hasDynamicValue}
        onSelect={onDynamicValueSelect}
      />
    </div>
  );
}

DynamicDatePicker.propTypes = {
  type: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  parameter: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  onSelect: PropTypes.func,
  dynamicButtonOptions: PropTypes.shape({
    staticValueLabel: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.object,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      })
    ),
  }),
  dateOptions: PropTypes.any, // eslint-disable-line react/forbid-prop-types
};

DynamicDatePicker.defaultProps = {
  type: "",
  className: "",
  value: null,
  parameter: null,
  dynamicButtonOptions: {
    options: [],
  },
  onSelect: () => {},
};

export default DynamicDatePicker;
